import { useState } from 'react';
import { Tooltip, Grid, ClickAwayListener } from '@material-ui/core';
import { Button } from '#root/components/ui/primitives';
import {
  IconTruckDelivery,
  IconCreditCard,
  IconCalendarEvent,
  IconPackage,
  IconMapPin,
  IconUserCheck,
} from '@tabler/icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { ShippingStatus } from '#root/models/Shipping';
import { useResponsive } from '#root/hooks';
import { changeCurrencySymbol } from '#root/utils';
import { GuveryPaper } from '#root/ui-components/cards';
import ShipmentStatusBadge from '../ShipmentStatusBadge';
import styles from './shipmentCard.module.scss';

const ShipInfo = ({ title, icon, content }) => (
  <div className={styles.shipInfo}>
    <p className={styles.title}>{title}</p>
    <div className={styles.content}>
      {icon}
      <h6 className={styles.text}>{content}</h6>
    </div>
  </div>
);

interface ShipmentCardProps {
  shipment: any;
  handleOpenPaymentDialog: (shipment: any) => void;
  handleOpenAddressDrawer: (shipment: any) => void;
  handleOpenPickupDrawer: (shipment: any) => void;
  handleOpenDetailDialog: (shipment: any) => void;
}

const ShipmentCard = (props: ShipmentCardProps) => {
  const { shipment, handleOpenPaymentDialog, handleOpenAddressDrawer, handleOpenPickupDrawer, handleOpenDetailDialog } =
    props;
  const { isMobile } = useResponsive();
  const [openTool, setOpenToool] = useState(false);

  const handleTooltipClose = () => {
    setOpenToool(false);
  };

  const handleTooltipOpen = () => {
    setOpenToool(true);
  };

  const address = shipment?.delivery_address?.address_1;
  const adrDepartment = shipment?.delivery_address?.department;
  const adrDistrict = shipment?.delivery_address?.district;

  // Pickup office info
  const pickupOffice = shipment?.pickup_office;

  const pickupPerson = shipment?.picked_by_doc && shipment?.picked_by_name ? `${shipment.picked_by_name} (${shipment.picked_by_doc})` : '';

  const dateShipped = shipment.shipped_at
    ? format(new Date(shipment.shipped_at.replace(/ /g, 'T')), "dd 'de' MMMM 'del' yyyy", {
      locale: es,
    })
    : '';

  const dateClosedDebited =
    shipment?.is_closed_debited && shipment?.closed_at
      ? format(new Date(shipment.closed_at.replace(/ /g, 'T')), "dd 'de' MMMM 'del' yyyy", {
        locale: es,
      })
      : '';

  const currency = shipment.currency && changeCurrencySymbol(shipment.currency, 'Symbol');

  const isCreated = shipment?.current_status === ShippingStatus.created;
  const isRegistered = shipment?.current_status === ShippingStatus.registered;
  const isForPickup = shipment?.current_status === ShippingStatus.for_pickup;
  const isPickupAuthorized = shipment?.current_status === ShippingStatus.pickup_authorized;
  const isPicked = shipment?.current_status === ShippingStatus.picked;
  const isConfirmed = shipment?.current_status === ShippingStatus.confirmed;
  const isProcesando = shipment?.current_status === ShippingStatus.processing;
  const isReady = shipment?.current_status === ShippingStatus.ready;
  const isShipped = shipment?.current_status === ShippingStatus.shipped;
  const isClosedDebited = shipment?.is_closed_debited;

  const needPayment = (isReady || isShipped || isClosedDebited) && !shipment.paid_at;
  // Mostrar Detalle cuando no es REGISTRADO (que tiene sus propios botones)
  const showDetailButton = !isRegistered;

  const isPay =
    shipment?.current_status === ShippingStatus.paid ||
    shipment?.current_status === ShippingStatus.paid_order ||
    shipment?.current_status === ShippingStatus.paying;


  const officeTitle = isRegistered ? 'Oficina actual' : (isForPickup || isPicked || isPickupAuthorized ? 'Oficina de recogida' : 'Oficina de procesamiento');

  const addressNode = (
    <span>
      <span>{address}, </span>
      <span className="guv-force-capitalize">{adrDepartment}, </span>
      <span className="guv-force-capitalize">{adrDistrict}</span>
    </span>
  );

  return (
    <GuveryPaper disableHover>
      <div className={styles.root}>

        <div className={styles.body}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              {(isRegistered || needPayment) && <span className={styles.dot}></span>}
              <h4 className={styles.id}>#{shipment.id}</h4>
              <ShipmentStatusBadge
                status={shipment?.current_status}
                paid_at={shipment?.paid_at}
                shipped_at={shipment?.shipped_at}
                is_closed_debited={shipment?.is_closed_debited}
              />
            </div>
            {isMobile && (
              <div className={styles.headerPrice}>
                {shipment?.amount && (
                  <div className={styles.price}>
                    <h4>{currency + ' ' + shipment.amount}</h4>
                  </div>
                )}
              </div>
            )}
          </div>


          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ShipInfo
                title="Descripción"
                icon={<IconPackage size={18} stroke={1.5} />}
                content={<span>{shipment?.description ? shipment?.description : '...'}</span>}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <ShipInfo
                title={officeTitle}
                icon={<IconMapPin size={18} stroke={1.5} />}
                content={
                  <span>
                    {pickupOffice.name} - {pickupOffice.address}
                  </span>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {isForPickup || isPicked || isPickupAuthorized ? (
                <ShipInfo
                  title="Persona autorizada"
                  icon={<IconUserCheck size={18} stroke={1.5} />}
                  content={pickupPerson ? pickupPerson : 'No registrada'}
                />
              ) : (
                <ShipInfo
                  title="Destino"
                  icon={<IconTruckDelivery size={18} stroke={1.5} />}
                  content={
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <span className="guv-block">
                        {adrDepartment ? (
                          <Tooltip
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            arrow
                            title={addressNode}
                            open={openTool}
                            onClose={handleTooltipClose}>
                            <span
                              className="guv-force-capitalize guv-underline guv-pointer"
                              onClick={handleTooltipOpen}>
                              {adrDepartment}
                            </span>
                          </Tooltip>
                        ) : (
                          <span style={{ color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-sm)' }}>(por confirmar)</span>
                        )}
                      </span>
                    </ClickAwayListener>
                  }
                />
              )}

            </Grid>


            {dateShipped && (
              <Grid item xs={12} sm={6}>
                <ShipInfo
                  title="Fecha de despacho"
                  icon={<IconCalendarEvent size={18} stroke={1.5} />}
                  content={dateClosedDebited ? <span>{dateClosedDebited}</span> : <span>{dateShipped}</span>}
                />
              </Grid>
            )}

          </Grid>
        </div>
        <div className={styles.right}>
          {!isMobile && (
            <div className={styles.top}>
              {shipment?.amount && (
                <div className={styles.price}>
                  <h4>{currency + ' ' + shipment.amount}</h4>
                </div>
              )}
            </div>
          )}
          {/* Acciones */}
          <div className={styles.actions}>
            {needPayment && (
              <Button
                variant="primary"
                fullWidth
                icon={<IconCreditCard size={22} />}
                onClick={() => handleOpenPaymentDialog(shipment)}>
                Pagar
              </Button>
            )}
            {isRegistered && (
              <>
                <Button variant="outline" onClick={() => handleOpenPickupDrawer(shipment)}>
                  Recoger en persona
                </Button>
                <Button variant="primary" onClick={() => handleOpenAddressDrawer(shipment)}>
                  Confirmar Envío
                </Button>
              </>
            )}
            {showDetailButton && (
              <Button variant="outline" onClick={() => handleOpenDetailDialog(shipment)}>
                Detalle
              </Button>
            )}
          </div>
        </div>
      </div>
    </GuveryPaper>
  );
};

export default ShipmentCard;
